import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  TrendingUp, TrendingDown, Minus, Loader2, RefreshCw, Sparkles,
  AlertTriangle, CheckCircle, Info, XCircle, Search, BarChart3, Zap, Database, Clock, Settings, Trash2, CalendarDays, MessageSquareText, Pin, GripVertical
} from 'lucide-react';
import { toast } from 'sonner';
import { Textarea } from '@/components/ui/textarea';
import { InvestigatePanel } from '@/components/investigate/InvestigatePanel';
import ObjectChatDialog from '@/components/ObjectChatDialog';

const SEV_CONFIG = {
  critical: { icon: XCircle, color: 'text-red-400', bg: 'bg-red-500/10', border: 'border-red-500/20', dot: 'bg-red-500' },
  warning: { icon: AlertTriangle, color: 'text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-500/20', dot: 'bg-amber-500' },
  info: { icon: Info, color: 'text-blue-400', bg: 'bg-blue-500/10', border: 'border-blue-500/20', dot: 'bg-blue-500' },
  positive: { icon: CheckCircle, color: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/20', dot: 'bg-emerald-500' },
};

function MiniSparkline({ data, width = 80, height = 24 }) {
  if (!data || data.length < 2) return null;
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const points = data.map((v, i) => `${(i / (data.length - 1)) * width},${height - ((v - min) / range) * height}`).join(' ');
  const isUp = data[data.length - 1] >= data[0];
  return (
    <svg width={width} height={height} className="flex-shrink-0">
      <polyline points={points} fill="none" stroke={isUp ? '#4ade80' : '#f87171'} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export default function KpiMonitorPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [models, setModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState('');
  const [kpis, setKpis] = useState([]);
  const [pinnedKpis, setPinnedKpis] = useState([]);
  const [insights, setInsights] = useState([]);
  const [insightsSummary, setInsightsSummary] = useState('');
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [kpiLoading, setKpiLoading] = useState(false);
  const [timeDim, setTimeDim] = useState('');
  const [lastScanTime, setLastScanTime] = useState('');
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [scanSchedules, setScanSchedules] = useState([]);
  const [newInterval, setNewInterval] = useState(24);
  const [contextOpen, setContextOpen] = useState(false);
  const [dataContext, setDataContext] = useState({ data_valid_through: '', notes: '' });
  const [savingContext, setSavingContext] = useState(false);
  const [investigateOpen, setInvestigateOpen] = useState(false);
  const [investigateTarget, setInvestigateTarget] = useState(null);
  const [latestInvByMetric, setLatestInvByMetric] = useState({});
  const [kpiDiscIndex, setKpiDiscIndex] = useState({});
  const [kpiThread, setKpiThread] = useState({ open: false, kpi: null });
  useEffect(() => { api.getDiscussionIndex().then(r => setKpiDiscIndex(r?.by_kpi || {})).catch(() => {}); }, []);

  useEffect(() => {
    api.getSemanticModels().then(m => {
      setModels(m || []);
      const pub = m.find(mm => mm.status === 'published') || m[0];
      if (pub) { setSelectedModel(pub.model_id); loadData(pub.model_id); }
      else setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const loadData = async (modelId) => {
    setLoading(true);
    try {
      const [kpiRes, insightRes] = await Promise.all([
        api.aiKpiMonitor({ model_id: modelId }).catch(() => ({})),
        api.aiGetInsights(modelId).catch(() => ({})),
      ]);
      setKpis(kpiRes.kpis || []);
      setTimeDim(kpiRes.time_dimension || '');
      if (kpiRes.data_context) setDataContext(kpiRes.data_context);
      // Load the user's pinned metrics (persisted from Explore) for this model.
      api.aiPinnedKpiMonitor({ model_id: modelId })
        .then(r => setPinnedKpis(r?.kpis || []))
        .catch(() => setPinnedKpis([]));
      setInsights(insightRes.insights || []);
      // Fetch latest alert-fired investigation per metric so we can badge
      // each KPI card with an 'Explained by AI' pill.
      api.latestInvestigationPerMetric()
        .then((r) => setLatestInvByMetric(r?.metrics || {}))
        .catch(() => {});
      setInsightsSummary(insightRes.summary || '');
      setLastScanTime(insightRes.scanned_at || '');
      // Load scan schedules
      api.listScanSchedules().then(s => setScanSchedules(s || [])).catch(() => {});
    } catch {}
    finally { setLoading(false); }
  };

  const handleScan = async () => {
    if (!selectedModel) return;
    setScanning(true);
    try {
      const res = await api.aiAnomalyScan({ model_id: selectedModel });
      setInsights(res.insights || []);
      setInsightsSummary(res.summary || '');
      setLastScanTime(res.scanned_at || new Date().toISOString());
      toast.success(`Scan complete: ${(res.insights || []).length} insights found`);
    } catch (err) { toast.error(err.message); }
    finally { setScanning(false); }
  };

  const handleRefreshKpis = async () => {
    if (!selectedModel) return;
    setKpiLoading(true);
    try {
      const res = await api.aiKpiMonitor({ model_id: selectedModel });
      setKpis(res.kpis || []);
      setTimeDim(res.time_dimension || '');
    } catch {}
    finally { setKpiLoading(false); }
  };

  const handleModelChange = (modelId) => {
    setSelectedModel(modelId);
    loadData(modelId);
  };

  const handleUnpin = async (pinId) => {
    if (!pinId) return;
    try {
      await api.deleteKpiPin(pinId);
      setPinnedKpis(prev => prev.filter(k => k.pin_id !== pinId));
      toast.success('Unpinned from KPI Monitor');
    } catch (err) { toast.error(err.message); }
  };

  // Drag-and-drop reordering of pinned metrics (order saved per user).
  const [dragIndex, setDragIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const handleDrop = async (targetIndex) => {
    const from = dragIndex;
    setDragIndex(null);
    setDragOverIndex(null);
    if (from === null || from === targetIndex) return;
    const next = [...pinnedKpis];
    const [moved] = next.splice(from, 1);
    next.splice(targetIndex, 0, moved);
    setPinnedKpis(next);
    try {
      await api.reorderKpiPins({ model_id: selectedModel, pin_ids: next.map(k => k.pin_id) });
    } catch (err) { toast.error(err.message || 'Could not save order'); }
  };

  const formatVal = (v) => v === null || v === undefined ? '—' : typeof v === 'object' ? JSON.stringify(v) : typeof v === 'number' ? (Math.abs(v) >= 1000000 ? `${(v/1000000).toFixed(1)}M` : Math.abs(v) >= 1000 ? `${(v/1000).toFixed(1)}K` : v.toFixed(v % 1 === 0 ? 0 : 2)) : String(v);

  if (loading) return <div className="flex items-center justify-center h-full"><Loader2 className="w-8 h-8 text-primary animate-spin" /></div>;

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto pb-24 md:pb-6" data-testid="kpi-monitor-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-3">
        <div>
          <h1 className="text-xl md:text-2xl font-heading font-bold flex items-center gap-2">
            <Zap className="w-5 h-5 md:w-6 md:h-6 text-amber-400" /> KPI Monitor
          </h1>
          <p className="text-xs md:text-sm text-muted-foreground mt-1">Real-time metric tracking with AI anomaly detection</p>
        </div>
        <div className="flex items-center gap-1.5 md:gap-2 flex-wrap">
          <Select value={selectedModel} onValueChange={handleModelChange}>
            <SelectTrigger className="w-56" data-testid="kpi-model-select">
              <Database className="w-3.5 h-3.5 mr-1.5 text-muted-foreground" />
              <SelectValue placeholder="Select model..." />
            </SelectTrigger>
            <SelectContent>
              {models.map(m => <SelectItem key={m.model_id} value={m.model_id}>{m.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={handleRefreshKpis} disabled={kpiLoading} className="gap-1.5">
            <RefreshCw className={`w-3.5 h-3.5 ${kpiLoading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
          <Button size="sm" onClick={handleScan} disabled={scanning} className="gap-1.5 bg-indigo-500 hover:bg-indigo-600 text-white">
            {scanning ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
            {scanning ? 'Scanning...' : 'AI Scan'}
          </Button>
          <Button variant="outline" size="sm" onClick={() => setScheduleOpen(true)} className="gap-1.5">
            <Clock className="w-3.5 h-3.5" /> Schedule
          </Button>
          <Button variant="outline" size="sm" onClick={() => setContextOpen(true)} className="gap-1.5" data-testid="ai-context-btn">
            <CalendarDays className="w-3.5 h-3.5" /> AI Context
            {dataContext.data_valid_through && <div className="w-1.5 h-1.5 rounded-full bg-cyan-400 ml-0.5" />}
          </Button>
        </div>
      </div>

      {/* Data Context Banner */}
      {dataContext.data_valid_through && (
        <div className="mb-4 flex items-center gap-3 p-3 rounded-xl border border-cyan-500/20 bg-cyan-500/[0.05]" data-testid="data-context-banner">
          <CalendarDays className="w-4 h-4 text-cyan-400 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-xs font-medium text-cyan-300">
              Data valid through: {new Date(dataContext.data_valid_through + 'T00:00:00').toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
            <p className="text-[10px] text-muted-foreground mt-0.5">AI comparisons will account for partial period data</p>
          </div>
          {dataContext.notes && (
            <div className="flex items-center gap-1 text-[10px] text-muted-foreground max-w-[200px] truncate" title={dataContext.notes}>
              <MessageSquareText className="w-3 h-3 flex-shrink-0" /> {dataContext.notes}
            </div>
          )}
          <button onClick={() => setContextOpen(true)} className="text-[10px] text-cyan-400 hover:text-cyan-300 flex-shrink-0">Edit</button>
        </div>
      )}

      {/* Pinned Metrics — persisted from Explore */}
      {pinnedKpis.length > 0 && (
        <div className="mb-6" data-testid="pinned-kpis-section">
          <h2 className="text-sm font-heading font-semibold flex items-center gap-2 mb-3">
            <Pin className="w-4 h-4 text-amber-400" /> Pinned Metrics
            <Badge variant="outline" className="text-[9px]">{pinnedKpis.length}</Badge>
            {pinnedKpis.length > 1 && <span className="text-[10px] text-muted-foreground font-normal">· drag to reorder</span>}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {pinnedKpis.map((kpi, idx) => {
              const isUp = kpi.change >= 0;
              const isZero = kpi.change === 0;
              return (
                <div
                  key={kpi.pin_id || kpi.name}
                  draggable
                  onDragStart={() => setDragIndex(idx)}
                  onDragOver={(e) => { e.preventDefault(); if (dragOverIndex !== idx) setDragOverIndex(idx); }}
                  onDragLeave={() => setDragOverIndex(prev => (prev === idx ? null : prev))}
                  onDrop={() => handleDrop(idx)}
                  onDragEnd={() => { setDragIndex(null); setDragOverIndex(null); }}
                  className={`glass-card rounded-xl p-4 relative overflow-hidden border cursor-grab active:cursor-grabbing transition-all ${dragOverIndex === idx && dragIndex !== idx ? 'border-amber-400 ring-2 ring-amber-400/40' : 'border-amber-500/20'} ${dragIndex === idx ? 'opacity-50' : ''}`}
                  data-testid={`pinned-kpi-${kpi.name}`}
                >
                  <div className="absolute top-2.5 left-2 opacity-30" title="Drag to reorder"><GripVertical className="w-3.5 h-3.5" /></div>
                  <button
                    type="button"
                    onClick={() => handleUnpin(kpi.pin_id)}
                    className="absolute top-2.5 right-2 opacity-60 hover:opacity-100 transition-opacity px-1.5 py-0.5 rounded-md bg-amber-500/15 text-amber-300 border border-amber-500/30 text-[9px] flex items-center gap-1"
                    title="Unpin from KPI Monitor"
                    data-testid={`unpin-kpi-${kpi.name}`}
                  >
                    <Pin className="w-2.5 h-2.5 fill-current" /> Unpin
                  </button>
                  <p className="text-xs md:text-[10px] text-muted-foreground font-medium truncate pr-14 pl-4 mb-1" title={kpi.description || kpi.name}>
                    {kpi.description || kpi.name.replace(/_/g, ' ')}
                  </p>
                  <p className="text-2xl font-bold text-foreground tabular-nums">{formatVal(kpi.current)}</p>
                  <div className="flex items-center gap-2 mt-1.5">
                    <div className={`flex items-center gap-0.5 text-xs font-medium ${isZero ? 'text-muted-foreground' : isUp ? 'text-emerald-400' : 'text-red-400'}`}>
                      {isZero ? <Minus className="w-3 h-3" /> : isUp ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                      <span>{isZero ? '0' : `${isUp ? '+' : ''}${(kpi.change_pct ?? 0).toFixed(1)}%`}</span>
                    </div>
                    <div className="ml-auto"><MiniSparkline data={kpi.trend} /></div>
                  </div>
                  <p className="text-[9px] text-muted-foreground/70 mt-1">Previous: {formatVal(kpi.previous)}</p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* AI Insights Banner */}
      {insights.length > 0 && (
        <div className="mb-6 glass-card rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-heading font-semibold flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-indigo-400" /> AI Anomaly Insights
            </h2>
            <div className="flex items-center gap-2">
              {lastScanTime && <span className="text-[9px] text-muted-foreground">Last scan: {new Date(lastScanTime).toLocaleString()}</span>}
              <Badge variant="outline" className="text-[9px]">{insights.length} findings</Badge>
            </div>
          </div>
          {insightsSummary && <p className="text-xs text-muted-foreground mb-3">{insightsSummary}</p>}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {insights.map((ins, i) => {
              const cfg = SEV_CONFIG[ins.severity] || SEV_CONFIG.info;
              const Icon = cfg.icon;
              return (
                <button key={i} onClick={() => {
                  if (ins.suggested_query) {
                    sessionStorage.setItem('exploreAutoAsk', JSON.stringify({ model_id: selectedModel, question: ins.suggested_query }));
                    navigate('/ai-copilot');
                  }
                }} className={`${cfg.bg} ${cfg.border} border rounded-lg p-3 text-left hover:scale-[1.01] transition-all`}>
                  <div className="flex items-start gap-2">
                    <Icon className={`w-4 h-4 ${cfg.color} flex-shrink-0 mt-0.5`} />
                    <div className="flex-1 min-w-0">
                      <p className={`text-xs font-medium ${cfg.color}`}>{ins.title}</p>
                      <p className="text-[10px] text-muted-foreground mt-0.5 line-clamp-2">{ins.description}</p>
                      {ins.change_pct && <Badge variant="outline" className={`text-[8px] mt-1 ${ins.change_pct > 0 ? 'text-emerald-400 border-emerald-500/20' : 'text-red-400 border-red-500/20'}`}>{ins.change_pct > 0 ? '+' : ''}{ins.change_pct}%</Badge>}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* KPI Cards Grid */}
      {kpis.length === 0 ? (
        <div className="text-center py-16 glass-card rounded-xl">
          <BarChart3 className="w-12 h-12 text-muted-foreground/20 mx-auto mb-4" />
          <p className="text-lg font-medium text-muted-foreground">No KPIs available</p>
          <p className="text-sm text-muted-foreground/80 mt-1">Select a semantic model with measures to monitor</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
          {kpis.map((kpi, i) => {
            const isUp = kpi.change >= 0;
            const isZero = kpi.change === 0;
            const anomaly = kpi.anomaly;
            const anomCfg = anomaly ? SEV_CONFIG[anomaly] : null;
            return (
              <div key={kpi.name} className={`glass-card rounded-xl p-4 relative overflow-hidden transition-all hover:border-primary/20 group ${anomCfg ? anomCfg.border + ' border' : ''}`} data-testid={`kpi-card-${kpi.name}`}>
                {/* Anomaly dot */}
                {anomaly && <div className={`absolute top-3 right-3 w-2 h-2 rounded-full ${anomCfg.dot} animate-pulse`} title={`${anomaly} anomaly`} />}

                {/* Investigate Why trigger — appears on hover so it doesn't
                    steal focus from at-a-glance reading of the metric. */}
                <button
                  type="button"
                  onClick={(ev) => {
                    ev.stopPropagation();
                    setInvestigateTarget({
                      metric: kpi.name,
                      model_id: selectedModel,
                      change_pct: kpi.change_pct,
                      direction: (kpi.change_pct || 0) < 0 ? 'drop' : 'rise',
                      window: kpi.comparable_period ? `the last period vs prior ${kpi.comparable_through || 'comparable'}` : 'the last period',
                      source: 'kpi_monitor',
                    });
                    setInvestigateOpen(true);
                  }}
                  className={`absolute ${anomaly ? 'top-6' : 'top-2.5'} right-2 opacity-0 group-hover:opacity-100 transition-opacity px-1.5 py-0.5 rounded-md bg-indigo-500/15 text-indigo-300 border border-indigo-500/30 text-[9px] flex items-center gap-1 hover:bg-indigo-500/25`}
                  title="Investigate Why"
                  data-testid={`investigate-kpi-${kpi.name}`}
                >
                  <Sparkles className="w-2.5 h-2.5" />
                  Investigate
                </button>
                
                {/* Label */}
                <p className="text-xs md:text-[10px] text-muted-foreground font-medium truncate pr-4 mb-1" title={kpi.description || kpi.name}>
                  {kpi.description || kpi.name.replace(/_/g, ' ')}
                </p>
                
                {/* Value */}
                <p className="text-2xl font-bold text-foreground tabular-nums">
                  {formatVal(kpi.current)}
                </p>
                
                {/* Change */}
                <div className="flex items-center gap-2 mt-1.5">
                  <div className={`flex items-center gap-0.5 text-xs font-medium ${isZero ? 'text-muted-foreground' : isUp ? 'text-emerald-400' : 'text-red-400'}`}>
                    {isZero ? <Minus className="w-3 h-3" /> : isUp ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                    <span>{isZero ? '0' : `${isUp ? '+' : ''}${kpi.change_pct.toFixed(1)}%`}</span>
                  </div>
                  <span className="text-[9px] text-muted-foreground/70">{kpi.comparable_period ? `vs prior ${kpi.comparable_through || 'comparable'}` : dataContext.data_valid_through ? 'vs comparable prior' : 'vs prev period'}</span>
                  <div className="ml-auto">
                    <MiniSparkline data={kpi.trend} />
                  </div>
                </div>
                
                {/* Previous value */}
                <p className="text-[9px] text-muted-foreground/70 mt-1">
                  Previous: {formatVal(kpi.previous)}
                </p>

                {/* Latest alert-fired investigation link */}
                {latestInvByMetric[kpi.name] && (
                  <button
                    type="button"
                    onClick={(ev) => {
                      ev.stopPropagation();
                      navigate(`/investigations?open=${latestInvByMetric[kpi.name].investigation_id}`);
                    }}
                    title={latestInvByMetric[kpi.name].verdict_summary || 'Open the AI investigation triggered by the last breach'}
                    className="mt-2 w-full text-[10px] px-2 py-1 rounded-md border border-indigo-500/25 bg-indigo-500/10 text-indigo-300 hover:bg-indigo-500/20 flex items-center gap-1 truncate transition-colors"
                    data-testid={`kpi-explained-by-ai-${kpi.name}`}
                  >
                    <Sparkles className="w-2.5 h-2.5 flex-none" />
                    <span className="truncate">
                      {latestInvByMetric[kpi.name].status === 'completed' ? 'Latest breach explained by AI' : `AI investigation ${latestInvByMetric[kpi.name].status}`}
                    </span>
                  </button>
                )}

                {/* Discussion indicator / start a thread on this KPI */}
                <div className="flex items-center gap-1.5 mt-2">
                  {kpiDiscIndex[kpi.name]?.total > 0 && (
                    <button
                      type="button"
                      onClick={(ev) => { ev.stopPropagation(); navigate(`/threads?id=${kpiDiscIndex[kpi.name].discussion_id}`); }}
                      className="text-[10px] px-2 py-1 rounded-md border border-primary/25 bg-primary/10 text-primary hover:bg-primary/20 flex items-center gap-1 transition-colors"
                      data-testid={`kpi-discussion-indicator-${kpi.name}`}
                    >
                      <MessageSquareText className="w-2.5 h-2.5" />{kpiDiscIndex[kpi.name].total} discussion{kpiDiscIndex[kpi.name].total === 1 ? '' : 's'}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={(ev) => { ev.stopPropagation(); setKpiThread({ open: true, kpi }); }}
                    className="text-[10px] px-2 py-1 rounded-md border border-border/40 text-muted-foreground hover:text-foreground hover:border-border/60 flex items-center gap-1 transition-colors"
                    data-testid={`kpi-discuss-${kpi.name}`}
                  >
                    <MessageSquareText className="w-2.5 h-2.5" /> Discuss
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Footer */}
      {kpis.length > 0 && (
        <div className="flex items-center justify-between mt-4 text-[9px] text-muted-foreground">
          <span>{kpis.length} metrics monitored{timeDim ? ` by ${timeDim.replace(/_/g, ' ')}` : ''}</span>
          <button onClick={() => navigate('/ai-copilot')} className="flex items-center gap-1 text-primary hover:text-primary/80">
            <Search className="w-3 h-3" /> Explore data deeper
          </button>
        </div>
      )}

      {/* Schedule Dialog */}
      <Dialog open={scheduleOpen} onOpenChange={setScheduleOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Clock className="w-5 h-5 text-primary" /> Auto-Scan Schedule</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <p className="text-sm text-muted-foreground">Configure automatic anomaly scans for your data models. The system will scan and notify you of critical findings.</p>
            
            {/* Existing schedules */}
            {scanSchedules.length > 0 && (
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Active Schedules</Label>
                {scanSchedules.map(s => (
                  <div key={s.schedule_id} className="flex items-center gap-2 p-2 rounded-lg border border-border/30 text-xs">
                    <div className={`w-2 h-2 rounded-full ${s.enabled ? 'bg-emerald-400' : 'bg-muted-foreground/30'}`} />
                    <span className="flex-1">Every {s.interval_hours}h</span>
                    <span className="text-muted-foreground">{s.last_run ? `Last: ${new Date(s.last_run).toLocaleDateString()}` : 'Not run yet'}</span>
                    <Switch checked={s.enabled} onCheckedChange={async (v) => {
                      await api.updateScanSchedule(s.schedule_id, { enabled: v });
                      setScanSchedules(prev => prev.map(ss => ss.schedule_id === s.schedule_id ? { ...ss, enabled: v } : ss));
                    }} />
                    <button onClick={async () => {
                      await api.deleteScanSchedule(s.schedule_id);
                      setScanSchedules(prev => prev.filter(ss => ss.schedule_id !== s.schedule_id));
                    }} className="text-muted-foreground/70 hover:text-destructive"><Trash2 className="w-3 h-3" /></button>
                  </div>
                ))}
              </div>
            )}
            
            {/* Create new schedule */}
            <div className="border-t border-border/30 pt-3">
              <Label className="text-xs text-muted-foreground">New Schedule</Label>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-sm text-muted-foreground">Scan every</span>
                <Select value={String(newInterval)} onValueChange={v => setNewInterval(Number(v))}>
                  <SelectTrigger className="w-28"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="6">6 hours</SelectItem>
                    <SelectItem value="12">12 hours</SelectItem>
                    <SelectItem value="24">Daily</SelectItem>
                    <SelectItem value="168">Weekly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={async () => {
                try {
                  const s = await api.createScanSchedule({ interval_hours: newInterval, notify_on_critical: true });
                  setScanSchedules(prev => [...prev, s]);
                  toast.success('Schedule created');
                } catch (err) { toast.error(err.message); }
              }} className="w-full mt-3 gap-2" size="sm"><Clock className="w-3.5 h-3.5" /> Create Schedule</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* AI Context Dialog */}
      <Dialog open={contextOpen} onOpenChange={setContextOpen}>
        <DialogContent className="sm:max-w-md" data-testid="ai-context-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><CalendarDays className="w-5 h-5 text-cyan-400" /> AI Data Context</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <p className="text-sm text-muted-foreground">
              Provide context about your data so AI comparisons are accurate. For example, if your current year only has data through April, the AI will compare Jan–Apr against prior year Jan–Apr instead of full-year totals.
            </p>

            <div className="space-y-2">
              <Label className="text-xs">Data valid through</Label>
              <Input
                type="date"
                value={dataContext.data_valid_through || ''}
                onChange={e => setDataContext(prev => ({ ...prev, data_valid_through: e.target.value }))}
                data-testid="context-date-input"
              />
              <p className="text-[10px] text-muted-foreground">The last date through which data is complete. The AI will not compare partial current periods against full prior periods.</p>
            </div>

            <div className="space-y-2">
              <Label className="text-xs">Additional notes for AI</Label>
              <Textarea
                value={dataContext.notes || ''}
                onChange={e => setDataContext(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="e.g., Q2 2025 data is preliminary and may be revised. Exclude Region X from comparisons as it was acquired in 2024."
                rows={3}
                className="text-sm resize-none"
                data-testid="context-notes-input"
              />
              <p className="text-[10px] text-muted-foreground">Any additional context the AI should consider when analyzing your data and generating insights.</p>
            </div>

            <div className="flex items-center gap-2 pt-2">
              <Button
                onClick={async () => {
                  setSavingContext(true);
                  try {
                    await api.updateSemanticModel(selectedModel, { data_context: dataContext });
                    toast.success('AI context saved');
                    setContextOpen(false);
                    // Re-scan with new context
                    toast.info('Re-running AI scan with new context...');
                    handleScan();
                  } catch (err) { toast.error(err.message); }
                  finally { setSavingContext(false); }
                }}
                disabled={savingContext}
                className="flex-1 gap-2"
                data-testid="save-context-btn"
              >
                {savingContext ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                Save & Re-scan
              </Button>
              {dataContext.data_valid_through && (
                <Button
                  variant="outline"
                  onClick={async () => {
                    setSavingContext(true);
                    try {
                      await api.updateSemanticModel(selectedModel, { data_context: {} });
                      setDataContext({ data_valid_through: '', notes: '' });
                      toast.success('AI context cleared');
                      setContextOpen(false);
                    } catch (err) { toast.error(err.message); }
                    finally { setSavingContext(false); }
                  }}
                  disabled={savingContext}
                  className="gap-1.5"
                  data-testid="clear-context-btn"
                >
                  <Trash2 className="w-3.5 h-3.5" /> Clear
                </Button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
      <InvestigatePanel open={investigateOpen} onOpenChange={setInvestigateOpen} target={investigateTarget} />
      <ObjectChatDialog
        open={kpiThread.open}
        onOpenChange={(v) => setKpiThread(t => ({ ...t, open: v }))}
        objectType="kpi"
        objectId={kpiThread.kpi?.name}
        label={kpiThread.kpi ? `${(kpiThread.kpi.name || '').replace(/_/g, ' ')} — discussion` : 'KPI discussion'}
      />
    </div>
  );
}
